import React, { useEffect } from 'react';
import {
    Users,
    ImageOff,
    Rocket,
    Megaphone,
    ShieldAlert,
    Bot,
    Compass,
    Flame,
    Clapperboard,
    FileText,
    LayoutDashboard,
    BarChart3,
    Filter,
    Handshake,
    ListChecks,
    Video,
    Eye,
    PhoneCall,
    CheckCircle2,
    XCircle,
    TrendingUp,
    Sparkles
} from 'lucide-react';

/* Reusable card: icon + heading + subheading, nothing else */
const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        },
        amber: {
            bg: "bg-amber-900/10",
            border: "border-amber-500/30",
            iconBg: "bg-amber-500/10",
            iconColor: "text-amber-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

export default function JayakrishnanPersonalBrand() {

    useEffect(() => {
        document.title = "Jayakrishnan G | Personal Brand + Launch Engine";
        window.scrollTo(0, 0);
    }, []);

    const steps = [
        { n: '01', h: 'Agreement, then the first payment', s: 'The guarantee is written into a legally bound agreement before any money moves. You sign with the number on the table.' },
        { n: '02', h: '7-day onboarding', s: 'Questionnaire, your story and your enemy, competitor research, positioning, content pillars. The guarantee number is locked here, after research, not before.' },
        { n: '03', h: 'The engine runs on your existing account', s: 'Same audience, no restart. Ideation, scripting, recording at your studio, editing, posting. The dashboard shows everything, the AI setter works your DMs.' },
        { n: '04', h: 'The launch, aimed at a real audience', s: 'Weeks 9 to 12: the out-of-the-box portal launch you asked for, fired at an audience that now exists instead of an empty room.' }
    ];

    const lanes = [
        { icon: Flame, h: 'The enemy lane', s: 'Payment delays of 45 to 90 days, sometimes 6 months. You say out loud what every freelancer feels and cannot say. This is the lane that builds the cult following.' },
        { icon: Compass, h: 'The mentor lane', s: 'Freelancer problems solved: portfolio building, pricing, handling clients. The exact content you described on the call, built for trust.' },
        { icon: Clapperboard, h: 'The builder lane', s: 'BTS from your studio, the portal built in public, the launch countdown. Your production work becomes the proof.' },
        { icon: ShieldAlert, h: 'The AI-consent lane', s: 'Models losing their faces to AI campaigns they never shot. You push the standard, and your AI-workforce feature becomes the ethical option.' }
    ];

    const wont = [
        { h: 'A 100% AI clone', s: 'You asked for a mix and that is what is written in. Real recorded content is the default because you own a studio. AI assists only where it is invisible.' },
        { h: 'Your middle and bottom funnel', s: 'We build the audience and bring qualified conversations. Closing brands and onboarding talent stays yours, exactly as clarified on the call.' },
        { h: 'A guarantee number before research', s: 'Kush was 10,000 in 90 days from a 100-follower start. You start with a base, so the researched number aims higher. It is locked in onboarding, in writing.' }
    ];

    const will = [
        { h: 'A minimum guarantee in writing', s: 'A follower-growth floor in a legally bound agreement. Miss it and 100% of your money comes back.' },
        { h: 'Same account, no restart', s: 'Your audience is already the right audience. We add distribution on top of the portfolio you built.' },
        { h: 'A launch with an audience waiting', s: 'The portal launch campaign lands in month 3, aimed at the following built in months 1 and 2.' }
    ];

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Jayakrishnan G</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Private</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Confidential · Private Proposal
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        The man fixing<br />how fashion pays its talent.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed mb-16 font-light">
                        A personal brand for Jayakrishnan G that builds the trust his talent portal will launch on.<br className="hidden sm:block" /> Real content first, from his own studio, run end to end by us.
                    </p>

                    <div className="max-w-md mx-auto">
                        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-6 py-8">
                            <div className="text-[10px] sm:text-xs text-white/40 font-bold tracking-widest uppercase mb-3">Investment · Monthly</div>
                            <div className="text-4xl sm:text-5xl font-black text-white tracking-tight">₹1.5 Lakh</div>
                            <div className="text-xs sm:text-sm text-white/50 mt-2">per month · 3-month minimum tenure · guarantee written into a legally bound agreement</div>
                        </div>
                    </div>
                </section>

                {/* THE PROCESS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The process</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            From yes<br />to a running engine.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You asked for the execution plan and the calendar. The calendar is an output of the research, so here is the exact order everything happens in.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {steps.map((step) => (
                            <div key={step.n} className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
                                <div className="text-2xl sm:text-3xl font-black text-indigo-400/80 shrink-0 w-10 sm:w-12 tabular-nums">{step.n}</div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{step.h}</h3>
                                    <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{step.s}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* PAIN POINTS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The honest read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Where you are right now.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Users} heading="The business runs on people you know" subheading="A decade of word-of-mouth got you here · a marketplace needs strangers, not just friends" accent="rose" />
                        <InfoCard icon={ImageOff} heading="Your Instagram is a portfolio, not an engine" subheading="Strong stills, almost no reels · a landing page waits for traffic, content creates it" accent="rose" />
                        <InfoCard icon={Rocket} heading="A launch with no one watching" subheading="The portal and app go live soon · a launch only lands as hard as the audience that sees day one" accent="rose" />
                        <InfoCard icon={Megaphone} heading="The enemy nobody with reach is naming" subheading="45 to 90 day payment cycles, sometimes 6 months · every freelancer feels it, no one says it out loud" accent="rose" />
                        <InfoCard icon={ShieldAlert} heading="Models losing their faces to AI" subheading="Basic shoots recycled into AI campaigns they never agreed to · the consent standard has no champion yet" accent="rose" />
                        <InfoCard icon={Bot} heading="The credibility trap of full-AI content" subheading="Your concern, and it is right · a trust-first brand cannot be built on content that reads as fake" accent="rose" />
                    </div>
                </section>

                {/* THE STRATEGY: FOUR LANES */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">The strategy</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Four content lanes.<br />One common enemy.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            People follow personality and conviction, not information. Your conviction is that talent should get paid on time and AI should work for them, not against them. Every lane feeds that.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 max-w-4xl mx-auto">
                        {lanes.map((lane) => (
                            <InfoCard key={lane.h} icon={lane.icon} heading={lane.h} subheading={lane.s} accent="amber" />
                        ))}
                    </div>
                </section>

                {/* WHAT WE BUILD */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">Our side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we build and run.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-6xl mx-auto">
                        <InfoCard icon={Compass} heading="Market research first" subheading="Competitors reaching your audience today, which reels pull, where your gap is · the strategy is an output, not a template" accent="indigo" />
                        <InfoCard icon={FileText} heading="Complete done for you" subheading="Ideation, research, scripting, recording support, editing, posting · overall complete managing" accent="indigo" />
                        <InfoCard icon={Clapperboard} heading="Real content first, AI where invisible" subheading="Your studio and BTS are the moat · AI assists on b-roll and repurposing, never the face of the brand" accent="indigo" />
                        <InfoCard icon={LayoutDashboard} heading="The content dashboard" subheading="Calendar view of drafts, editing, ready to post · you see the machine running at all times" accent="indigo" />
                        <InfoCard icon={BarChart3} heading="6,000 posts of data monthly" subheading="Every hook and format we publish is tracked · what works elsewhere gets rebuilt for your niche" accent="indigo" />
                        <InfoCard icon={Filter} heading="DMs become conversations" subheading="The Meta-verified AI setter talks to your leads, understands their problem and books the call" accent="indigo" />
                        <InfoCard icon={Rocket} heading="The launch campaign" subheading="Month 3: the out-of-the-box portal launch, waitlist content and the talent onboarding push" accent="indigo" />
                        <InfoCard icon={Handshake} heading="Top of funnel only" subheading="Audience, trust and booked conversations from us · closing brands and talent stays yours" accent="indigo" />
                    </div>
                </section>

                {/* YOUR SIDE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">Your side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we need from you.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Lighter than it sounds. You already run a production studio, so recording folds into shoot days you are doing anyway.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={ListChecks} heading="20 minutes, yourself" subheading="The onboarding questionnaire works when it is you answering · the strategy is built on it" accent="emerald" />
                        <InfoCard icon={Video} heading="Recording days at your studio" subheading="Batch recording on days the studio is already live · BTS captures itself" accent="emerald" />
                        <InfoCard icon={Eye} heading="Approve before it ships" subheading="Verify content in the early weeks · most founders go hands-off once the rhythm sets" accent="emerald" />
                        <InfoCard icon={PhoneCall} heading="Take the conversations" subheading="The setter books qualified talent and brands · onboarding them stays yours" accent="emerald" />
                    </div>
                </section>

                {/* PROOF */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The proof</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            The samples you asked for.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 max-w-4xl mx-auto">
                        <InfoCard icon={TrendingUp} heading="Kush · AI content creator" subheading="100 to 200 followers at the start · now 38,000 on Instagram, 10,000 on YouTube, roughly 50,000 total day-one distribution for anything he launches" accent="indigo" />
                        <InfoCard icon={Sparkles} heading="Suresh's own page" subheading="Built deliberately small and sharp for founders · Raj Shamani messaged asking for the service, creators with lakhs of followers follow it" accent="indigo" />
                    </div>

                    <div className="text-center mt-8">
                        <a href="https://socialdeck.socialseo.in/case-studies" target="_blank" rel="noreferrer" className="text-indigo-300/80 hover:text-indigo-200 transition text-sm font-medium">
                            See the full case studies →
                        </a>
                    </div>
                </section>

                {/* PROMISE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">The exchange</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we promise.<br />And what we won't.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto">
                        {/* WON'T */}
                        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 sm:p-9">
                            <div className="text-rose-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we won't promise</div>
                            <ul className="space-y-6">
                                {wont.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <XCircle className="w-5 h-5 text-rose-400/70 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/50 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* WILL */}
                        <div className="rounded-3xl border border-indigo-500/30 bg-indigo-900/10 p-7 sm:p-9">
                            <div className="text-indigo-300/90 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we do promise</div>
                            <ul className="space-y-6">
                                {will.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <CheckCircle2 className="w-5 h-5 text-indigo-300 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pt-4 sm:pt-8">
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight mb-6">
                        The agreement is the next step.
                    </h2>
                    <p className="text-base sm:text-lg text-white/55 max-w-xl mx-auto font-light leading-relaxed mb-10">
                        Reply on WhatsApp or email and the agreement follows, guarantee written in. Sign, make the first payment, and the 7-day onboarding starts.
                    </p>
                    <a
                        href="mailto:suresh@socialseo.in"
                        className="inline-block bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-8 py-4 rounded-full text-sm sm:text-base"
                    >
                        Write to suresh@socialseo.in
                    </a>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
